import { useTranslation } from 'react-i18next';

import PropertyMultiSelectField from 'components/forms/domain/property/PropertyMultiSelectField/PropertyMultiSelectField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import {
  IsSigned,
  ConditionType,
  GuestsNumberOperator,
  StayLengthOperator,
  IsCompleted,
} from 'models/automation/Triggers';
import ChannelMultiSelectField from '../../common/ChannelMultiSelectField';

interface TriggerAddEditModalConditionsExtraFieldsProps {
  conditionType: ConditionType;
  index: number;
}

const TriggerAddEditModalConditionsExtraFields = ({
  conditionType,
  index,
}: TriggerAddEditModalConditionsExtraFieldsProps) => {
  const { t } = useTranslation();

  const isSignedOptions = Object.values(IsSigned).map((context) => ({
    label: t('pageAutomations.triggers.isSigned', { context }),
    value: context,
  }));

  const isCompletedOptions = Object.values(IsCompleted).map((context) => ({
    label: t('pageAutomations.triggers.isCompleted', { context }),
    value: context,
  }));

  const guestNumberOptions = Object.values(GuestsNumberOperator).map(
    (context) => ({
      label: t('pageAutomations.triggers.guestsNumberOperator', { context }),
      value: context,
    }),
  );

  const stayLengthOptions = Object.values(StayLengthOperator).map(
    (context) => ({
      label: t('pageAutomations.triggers.stayLengthOperator', { context }),
      value: context,
    }),
  );

  if (conditionType === ConditionType.AGREEMENT_SIGNED) {
    return (
      <SelectField
        aria-label={t('pageAutomations.triggers.isSigned')}
        colSmInput={12}
        defaultValue={IsSigned.IS_SIGNED}
        name={`conditions.${index}.operator`}
        options={isSignedOptions}
      />
    );
  }

  if (conditionType === ConditionType.BOOKING_PAID_IN_FULL) {
    return (
      <SelectField
        aria-label={t('pageAutomations.triggers.isCompleted')}
        colSmInput={12}
        defaultValue={IsCompleted.IS_COMPLETED}
        name={`conditions.${index}.operator`}
        options={isCompletedOptions}
      />
    );
  }

  if (conditionType === ConditionType.CHANNEL) {
    return (
      <ChannelMultiSelectField
        aria-label={t('common.channels')}
        colSmInput={12}
        name={`conditions.${index}.channels`}
      />
    );
  }

  if (conditionType === ConditionType.GUESTS_NUMBER) {
    return (
      <>
        <SelectField
          aria-label={t('pageAutomations.triggers.guestsNumberOperator')}
          colSmInput={12}
          defaultValue={GuestsNumberOperator.IS_GREATER_THAN}
          name={`conditions.${index}.operator`}
          options={guestNumberOptions}
        />
        <TextField
          step={1}
          aria-label={t('pageAutomations.triggers.modal.guests')}
          colSmInput={12}
          defaultValue={1}
          name={`conditions.${index}.guests`}
          type="number"
          min={0}
        />
      </>
    );
  }

  if (conditionType === ConditionType.PRE_ARRIVAL_FORM_SUBMITTED) {
    return (
      <SelectField
        aria-label={t('pageAutomations.triggers.isCompleted')}
        colSmInput={12}
        defaultValue={IsCompleted.IS_COMPLETED}
        name={`conditions.${index}.operator`}
        options={isCompletedOptions}
      />
    );
  }

  if (conditionType === ConditionType.PROPERTY) {
    return (
      <PropertyMultiSelectField
        aria-label={t('pageAutomations.triggers.filters.properties')}
        colSmInput={12}
        enableHotels={false}
        enableUnitTypes
        isSelectAllEnabled
        name={`conditions.${index}.propertyUids`}
        placeholder={t('common.selectOption')}
      />
    );
  }

  if (conditionType === ConditionType.STAY_LENGTH) {
    return (
      <>
        <SelectField
          aria-label={t('pageAutomations.triggers.stayLengthOperator')}
          colSmInput={12}
          defaultValue={StayLengthOperator.IS_LONGER}
          name={`conditions.${index}.operator`}
          options={stayLengthOptions}
        />
        <TextField
          step={1}
          aria-label={t('common.nights')}
          colSmInput={12}
          defaultValue={1}
          name={`conditions.${index}.days`}
          type="number"
          min={0}
        />
        <p className="condition-help-text">{t('common.nights')}</p>
      </>
    );
  }

  return null;
};

export default TriggerAddEditModalConditionsExtraFields;
